import React, { useMemo } from 'react'
import {
    Box,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'
import { TaskAltRounded } from '@mui/icons-material'
import dayjs from 'dayjs'

const ALARM_COLOR = '#F59E0B'
const DTC_COLOR = '#E00000'
const SEVERITY: Record<number, { label: string; color: string }> = {
    1: { label: 'Low', color: '#508991' },
    2: { label: 'Med', color: '#F59E0B' },
    3: { label: 'High', color: '#E00000' },
}

export interface FaultRow {
    type: 'alarm' | 'dtc'
    ts: number
    day?: string
    description: string
    code?: string
    severity?: number | null
    engineHours?: number | null
}

export type FaultFilterKey = 'all' | 'alarm' | 'dtc'

// Format a fault timestamp in a given IANA zone (undefined = the viewer's
// browser zone). Matches the "MMM D, HH:mm" style used across the app.
export const formatFaultTs = (ms: number, tz?: string) => {
    try {
        return new Date(ms).toLocaleString('en-US', {
            ...(tz ? { timeZone: tz } : {}),
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        })
    } catch {
        return dayjs(ms).format('MMM D, HH:mm')
    }
}

const accentFor = (r: { type: string; severity?: number | null }) =>
    r.type === 'dtc'
        ? (r.severity && SEVERITY[r.severity]?.color) || DTC_COLOR
        : ALARM_COLOR

// Alarm / DTC filter chips — rendered by the parent (e.g. inline with the tab
// row) so the filter can share a row with the tabs instead of taking its own.
export const FaultFilters: React.FC<{
    rows: FaultRow[]
    filter: FaultFilterKey
    onFilter: (f: FaultFilterKey) => void
}> = ({ rows, filter, onFilter }) => {
    const alarmCount = rows.filter((r) => r.type === 'alarm').length
    const dtcCount = rows.filter((r) => r.type === 'dtc').length
    const filters: { key: FaultFilterKey; label: string; color?: string }[] = [
        { key: 'all', label: `All (${alarmCount + dtcCount})` },
        { key: 'alarm', label: `Alarms (${alarmCount})`, color: ALARM_COLOR },
        { key: 'dtc', label: `DTCs (${dtcCount})`, color: DTC_COLOR },
    ]
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                flexWrap: 'wrap',
            }}
        >
            {filters.map((f) => {
                const active = filter === f.key
                return (
                    <Chip
                        key={f.key}
                        label={f.label}
                        size="small"
                        onClick={() => onFilter(f.key)}
                        aria-pressed={active}
                        sx={{
                            fontWeight: 600,
                            cursor: 'pointer',
                            color: active ? '#fff' : f.color || 'text.secondary',
                            bgcolor: active
                                ? f.color || 'primary.main'
                                : 'transparent',
                            border: '1px solid',
                            borderColor: f.color || 'divider',
                            '&:hover': {
                                bgcolor: active
                                    ? f.color || 'primary.main'
                                    : `${f.color || '#888'}1A`,
                            },
                        }}
                    />
                )
            })}
        </Box>
    )
}

// Flat fault occurrence log (newest first). Filtering is controlled by the
// parent (which renders <FaultFilters> wherever it wants the chips).
const FaultLog: React.FC<{
    rows: FaultRow[]
    filter?: FaultFilterKey
    windowLabel?: string
    /** IANA zone to render timestamps in (undefined = browser zone). */
    tz?: string
}> = ({ rows, filter = 'all', windowLabel, tz }) => {
    const shown = useMemo(
        () =>
            rows
                .filter((r) => filter === 'all' || r.type === filter)
                .slice()
                .sort((a, b) => b.ts - a.ts),
        [rows, filter]
    )

    return (
        <Box>
            {windowLabel && (
                <Typography
                    variant="body2"
                    sx={{ fontSize: 12, color: 'text.disabled', mb: 1.5 }}
                >
                    {windowLabel} · {shown.length} fault
                    {shown.length === 1 ? '' : 's'}
                </Typography>
            )}

            {shown.length === 0 ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 0.75,
                        py: 4,
                        color: 'text.secondary',
                    }}
                >
                    <TaskAltRounded
                        sx={{ fontSize: 26, color: 'success.main', opacity: 0.8 }}
                    />
                    <Typography variant="body2">
                        No{' '}
                        {filter === 'alarm'
                            ? 'alarms'
                            : filter === 'dtc'
                            ? 'DTCs'
                            : 'alarms or DTCs'}{' '}
                        in this period.
                    </Typography>
                </Box>
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 520 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>When</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Description</TableCell>
                                <TableCell align="right">Eng. hrs</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {shown.map((e, i) => {
                                const sev =
                                    e.type === 'dtc' && e.severity
                                        ? SEVERITY[e.severity]
                                        : null
                                const accent = accentFor(e)
                                return (
                                    <TableRow
                                        key={i}
                                        hover
                                        sx={{
                                            '& td': {
                                                borderLeft: 'none !important',
                                            },
                                            '& td:first-of-type': {
                                                borderLeft: `3px solid ${accent} !important`,
                                            },
                                        }}
                                    >
                                        <TableCell
                                            sx={{
                                                whiteSpace: 'nowrap',
                                                color: 'text.secondary',
                                            }}
                                        >
                                            {formatFaultTs(e.ts, tz)}
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={
                                                    e.type === 'dtc'
                                                        ? sev
                                                            ? `DTC · ${sev.label}`
                                                            : 'DTC'
                                                        : 'Alarm'
                                                }
                                                sx={{
                                                    height: 20,
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    color: accent,
                                                    bgcolor: `${accent}1A`,
                                                    border: '1px solid',
                                                    borderColor: accent,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {e.description}
                                            </Typography>
                                            {e.code && (
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontSize: 11,
                                                        color: 'text.disabled',
                                                    }}
                                                >
                                                    {e.code}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{
                                                whiteSpace: 'nowrap',
                                                color: 'text.disabled',
                                            }}
                                        >
                                            {e.engineHours != null
                                                ? `${e.engineHours.toLocaleString()} h`
                                                : '—'}
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </Box>
            )}
        </Box>
    )
}

export default FaultLog
